import _ from 'lodash';
import client from './RestClient';
import {
  Creative,
  CreativeApprovalConfig,
  CreativeBasic,
  CreativeBindingCampaign,
  CreativeListRecord,
  CreativeOfCampaign,
  CreativeSize,
  CreativeStatus,
  CreativeStatusFilter,
  CreativeType,
  isCombo,
  isNative,
  isOutdoor,
  isVideo,
  ServerState,
  VideoChildMap,
  videoNeedsProcessing
} from 'core/creative/Creative';
import { Pageable } from './Pageable';
import { Pagination } from 'core/pagination/Pagination';
import i18n from 'i18next';
import { Limitation, LimitationData } from 'core/limitation/Limitation';
import { L1ObjectChannel } from 'core/l1Object/L1Object';
import { AxiosInstance } from 'axios';
import { AdLogoType } from 'core/adLogo/AdLogo';

export interface CreativeWebService {
  getCreativesByCampaignId(
    campaignId: number,
    isDraft: boolean
  ): Promise<CreativeOfCampaign[]>;
  getWatingBindCreatives(
    payload: WaitingBindGetParams
  ): Promise<CreativeOfCampaign[]>;
  reviewCreatives(creativeIds: number[], adxs: string[]): Promise<void>;
  getCreative(creativeId: number): Promise<any>;
  createCreative(creativeformData: FormData): Promise<{
    creativeId: number;
  }>;
  createCreativesByJson(creatives: any): Promise<
    | {
        data: any[];
        error: {
          error_message: string | null;
          name: string;
        }[];
      }
    | undefined
  >;
  updateCreative(creativeformData: FormData): Promise<number>;
  getCreatives(
    creativeType: CreativeType,
    advertiserId: number | undefined,
    pageable: Pageable,
    search?: string,
    status?: CreativeStatusFilter
  ): Promise<CreativesWithPagination>;
  updateCreativeBindingStatus(creatives: number[], isActive: boolean);
  cloneCreatives(creatives: number[]): Promise<void>;
  deleteCreative(creativeIds: number[]): Promise<void>;
  updateCreativeBindingCampaigns(
    creativeId: number,
    campaigns: number[],
    isActive: boolean
  );
  getCreativeOptions(from?: string, to?: string): Promise<SelectOptions[]>;
  getCreativeSizeOptions(): Promise<SelectOptions[]>;
  getPpsLayoutId(): Promise<SelectOptions[]>;
  getPpsThumbnail(creativeId): Promise<string[]>;
  getCreativeNativeToBannerTemplate(): Promise<any>;
  getBindingOptions(): Promise<SelectOptions[]>;
  generateRectImgBySquareImg(imageUrl: string): Promise<string>;
  getAdneonCreativePreview(data: any): Promise<any>;
  publicVideoAssetId(
    videoAssetId: string,
    creativeType: CreativeType
  ): Promise<string>;
  getCreativeSizesByAdFormat(adFormat: string): Promise<CreativeSize[]>;
  checkCreativeSupportSchedule(creativeId: number): Promise<boolean>;
  getCreativeApprovalConfigs(): Promise<CreativeApprovalConfig[]>;
  getCreativeBindingStatus(
    creativeId: number
  ): Promise<CreativeBindingCampaign[]>;
}

export type WaitingBindGetParams = {
  channel: string;
  channelGroupId?: number;
  advertiserId: number;
  adType?: string;
  adTypeId?: number;
  supportSchedule?: boolean;
};

function wrapAdLogo(json: any) {
  if (!json) {
    return undefined;
  }
  return {
    type: _.get(json, 'type', AdLogoType.NULL),
    link: _.get(json, 'link'),
    image: _.get(json, 'imgUrl')
      ? {
          url: _.get(json, 'imgUrl')
        }
      : undefined
  };
}

function wrapCreativeBasic(json: any): CreativeBasic {
  const basic: CreativeBasic = {
    advertiserId: _.get(json, 'advertiserId'),
    agencyId: _.get(json, 'agencyId'),
    bannerUrl: _.get(json, 'bannerUrl'),
    creativeId: _.get(json, 'creativeId'),
    creativeType: _.get(json, 'creativeType'),
    creativeValues: _.get(json, 'creativeValues'),
    landingPageUrl: _.get(json, 'landingPageUrl'),
    name: _.get(json, 'name'),
    tenmaxCategory: _.get(json, 'tenmaxCategory'),
    bannerExtra: _.get(json, 'bannerExtra'),
    bannerImageUrl: _.get(json, 'bannerImageUrl'),
    enableStartTime: _.get(json, 'enableStartTime'),
    enableEndTime: _.get(json, 'enableEndTime'),
    ppsLayoutId: _.get(json, 'ppsLayoutId'),
    subLayout: _.get(json, 'subLayout'),
    adLogo: wrapAdLogo(_.get(json, 'adLogo'))
  };

  const nativeBanner = wrapNativeBanner(_.get(json, 'nativeBanner'));
  if (nativeBanner) {
    basic.nativeBanner = nativeBanner;
  }

  if (isNative(basic.creativeType)) {
    basic.enableNativeBanner = _.get(json, 'enableNativeBanner', false);
  }

  if (json.outdoorType) {
    basic.outdoorType = _.get(json, 'outdoorType', 1);
    basic.duration = _.get(
      json,
      'duration',
      _.get(json, 'creativeValues.duration', 30)
    );
  }

  return basic;
}

function wrapVideoChildren(json: any): VideoChildMap | undefined {
  const creativeType = _.get(json, 'creativeType');
  const creativeValue = _.get(
    json,
    'creativeValues',
    _.get(json, 'creativeValue')
  );
  if (!isVideo(creativeType) || creativeValue.adServingUrl) {
    return;
  }

  const videoChildenJson = _.get(json, 'videoChildren');
  if (!videoChildenJson || videoChildenJson.length === 0) {
    return;
  }

  return videoChildenJson.reduce((acc, videoChild) => {
    const deviceType = _.get(videoChild, 'deviceType');
    if (!deviceType) {
      return acc;
    }
    acc[i18n.t(`creativeList.labels.approval${_.capitalize(deviceType)}`)] = {
      creativeId: _.get(videoChild, 'creativeId'),
      videoUrl: _.get(videoChild, 'videoUrl'),
      approvalStatus: _.get(videoChild, 'approvalStatus')
    };
    return acc;
  }, {});
}

function wrapNativeBanner(json: any): { [key: string]: any } | undefined {
  if (!json || json.length === 0) {
    return;
  }
  let nativeBannerData = {};
  json.forEach((bannerData) => {
    if (_.isEmpty(bannerData)) {
      return;
    }
    nativeBannerData[`${bannerData.width}x${bannerData.height}`] = bannerData;
  });
  return nativeBannerData;
}

function wrapCreative(json: any): Creative {
  return {
    basic: wrapCreativeBasic(_.get(json, 'creative')),
    limitations: wrapLimitations(
      _.get(json, 'limitations', []),
      _.get(json, 'creative.dealIds')
    )
  };
}

function wrapCreativeState(json: any, videoChildren?: VideoChildMap) {
  const serverState = _.get(json, 'state');
  if (serverState === ServerState.ARCHIVED) {
    return CreativeStatus.ARCHIVED;
  }
  const status = _.get(json, 'creativeState');
  if (videoChildren) {
    if (Object.values(videoChildren).some((child) => !child.videoUrl)) {
      return CreativeStatus.PROCESSING;
    }
  }
  const creativeType = _.get(json, 'creativeType');
  const creativeValues = _.get(json, 'creativeValues');
  const outdoorType = _.get(json, 'outdoorType');
  if (
    videoNeedsProcessing(creativeType, outdoorType) &&
    !creativeValues.videoUrl
  ) {
    return CreativeStatus.PROCESSING;
  }
  if (
    [ServerState.PROCESSING, ServerState.PROCESSING_FAIL].includes(serverState)
  ) {
    return serverState;
  }
  return status;
}

function wrapCreativeListRecord(json: any): CreativeListRecord {
  const videoChildren = wrapVideoChildren(json);
  return {
    advertiserId: _.get(json, 'advertiserId'),
    approvalStatus: _.get(json, 'approvalStatus'),
    bannerImageUrl: _.get(json, 'bannerImageUrl'),
    bannerUrl: _.get(json, 'bannerUrl'),
    bindingCampaign: _.get(json, 'bindingCampaign'),
    bindingCount: _.get(json, 'bindingCount'),
    createTime: _.get(json, 'createTime'),
    creativeId: _.get(json, 'creativeId'),
    creativeState: wrapCreativeState(json, videoChildren),
    creativeType: _.get(json, 'creativeType'),
    creativeValues: _.get(json, 'creativeValues'),
    outdoorType: _.get(json, 'outdoorType'),
    duration: _.get(json, 'duration', _.get(json, 'creativeValues.duration')),
    ppsLayoutId: _.get(json, 'ppsLayoutId'),
    name: _.get(json, 'name'),
    tenmaxCategory: _.get(json, 'tenmaxCategory'),
    origTenmaxCategory: _.get(json, 'origTenmaxCategory'),
    limitations: wrapLimitations(
      _.get(json, 'limitations', []),
      _.get(json, 'creative.dealIds')
    ),
    bannerExtra: _.get(json, 'bannerExtra'),
    enableNativeBanner: _.get(json, 'enableNativeBanner', false),
    nativeBanner: wrapNativeBanner(_.get(json, 'nativeBanners')),
    enableStartTime: _.get(json, 'enableStartTime'),
    enableEndTime: _.get(json, 'enableEndTime'),
    subLayout: _.get(json, 'subLayout'),
    videoChildren
  };
}

function wrapWaitingBindCreative(json: any): CreativeOfCampaign {
  const vimpression = _.get(json, 'report.viewable', 0);
  const impression = _.get(json, 'report.impres', 0);
  const clicks = _.get(json, 'report.clicks', 0);
  const ctr = impression > 0 ? clicks / impression : 0;
  const vctr = vimpression > 0 ? clicks / vimpression : 0;
  const view = _.get(json, 'view', 0);
  const vtr = view > 0 ? view / impression : 0;
  const creativeType = _.get(json, 'creativeType');
  let size = _.get(json, 'width', 0) + 'X' + _.get(json, 'height', 0);
  if (isOutdoor(creativeType) || isCombo(creativeType)) {
    const videoW = _.get(json, 'creativeValues.videoW', 0);
    const videoH = _.get(json, 'creativeValues.videoH', 0);
    const imageW = _.get(json, 'creativeValues.imgW', 0);
    const imageH = _.get(json, 'creativeValues.imgH', 0);
    size = videoW + imageW + 'X' + (videoH + imageH);
  }
  const creativeId = _.get(json, 'creativeId');
  const status = _.get(json, 'status');
  const deilivery = _.get(json, 'deilivery');
  const rtbCreativeId = _.get(json, 'rtbCreativeId');
  const videoChildren = wrapVideoChildren(json);
  return {
    report: _.get(json, 'report'),
    rtbCreativeId: rtbCreativeId ? rtbCreativeId : _.get(json, 'creativeId'),
    id: creativeId ? creativeId : _.get(json, 'l3ChannelId'),
    name: _.get(json, 'creativeName'),
    status: wrapCreativeState(json, videoChildren),
    vctr,
    view,
    vtr,
    vimps: vimpression,
    ctr: ctr,
    imps: impression,
    clicks: clicks,
    size,
    creativeType,
    outdoorType: _.get(json, 'outdoorType'),
    enableStartTime: _.get(json, 'enableStartTime'),
    enableEndTime: _.get(json, 'enableEndTime'),
    approvalStatus: _.get(json, 'approvalStatus'),
    bannerImageUrl: _.get(json, 'bannerImageUrl'),
    creativeValue: _.get(json, 'creativeValues', {}),
    createTime: _.get(json, 'ctime'),
    bannerUrl: _.get(json, 'bannerUrl', ''),
    landingPageUrl: _.get(json, 'landingPageUrl', ''),
    isActiveBinding: _.get(json, 'isActiveBinding', true),
    bindingState: status ? status : _.get(json, 'bindingState', 0),
    tenmaxCategory: _.get(json, 'tenmaxCategory'),
    origTenmaxCategory: _.get(json, 'origTenmaxCategory'),
    shortcutUrl: _.get(json, 'shortcutUrl', ''),
    nativeBanner: wrapNativeBanner(_.get(json, 'nativeBanners')),
    effectiveStatus: deilivery ? deilivery : _.get(json, 'effectiveStatus'),
    issuesInfo: _.get(json, 'issuesInfo[0].error_message', ''),
    adReviewFeedback: _.get(json, 'adReviewFeedback', ''),
    l3ChannelId: _.get(json, 'l3ChannelId', ''),
    bannerExtra: _.get(json, 'bannerExtra'),
    previewLink: _.get(json, 'previewLink'),
    ppsLayoutId: _.get(json, 'ppsLayoutId'),
    subLayout: _.get(json, 'subLayout'),
    videoChildren
  };
}

function wrapCreativeWatingBindList(json: any): CreativeOfCampaign[] {
  return _.defaultTo(json, []).flatMap((json: any) =>
    wrapWaitingBindCreative(json)
  );
}

function wrapCreativList(json: any): CreativeListRecord[] {
  return _.defaultTo(json, []).flatMap((json: any) =>
    wrapCreativeListRecord(json)
  );
}

function wrapPagination(json: any): Pagination {
  return {
    page: _.get(json, 'page', 1),
    size: _.get(json, 'size', 10),
    totalCount: _.get(json, 'totalCount', 0)
  };
}

function wrapLimitation(json: any): Limitation {
  return {
    op: _.get(json, 'op'),
    value: _.get(json, 'limits', []).map((limitsJson) => {
      return _.omitBy(
        {
          label: _.get(limitsJson, 'label'),
          value: _.get(limitsJson, 'value'),
          isGroup: _.get(json, 'isGroup')
        },
        _.isUndefined
      );
    }),
    type: _.get(json, 'type')
  };
}

function wrapLimitations(limitations, dealIds) {
  const limitationMap = {
    inc: {},
    exc: {},
    Preferred: {},
    NonPreferred: {},
    other: {}
  };
  limitations.forEach((limitation) => {
    const wrappedLimitation = wrapLimitation(limitation);
    if (limitation.type === 'instl') {
      limitationMap[limitation.op]['adFormat'] = {
        op: limitation.op,
        type: 'adFormat',
        value: [
          {
            label: 'instl',
            value: 'instl'
          }
        ]
      };
    } else if (limitation.type in limitationMap[limitation.op]) {
      limitationMap[limitation.op][limitation.type].value = _.concat(
        limitationMap[limitation.op][limitation.type].value,
        wrappedLimitation.value
      );
    } else {
      limitationMap[limitation.op][limitation.type] = wrappedLimitation;
    }
  });

  const wrappedLimitations: LimitationData = {
    include: Object.values(limitationMap['inc']),
    exclude: Object.values(limitationMap['exc']),
    preferred: Object.values(limitationMap['Preferred']),
    nonPreferred: Object.values(limitationMap['NonPreferred']),
    other: Object.values(limitationMap['other'])
  };

  wrappedLimitations.other.push({
    op: 'inc',
    value: dealIds
      ? dealIds.map((dealId) => {
          return {
            label: dealId,
            value: dealId
          };
        })
      : [],
    type: 'dealId'
  });
  return wrappedLimitations;
}

function wrapPpsLayoutId(json) {
  const key = `ppsLayoutId.${_.get(json, 'layoutId', '').toLowerCase().replace(/-|\s/g, '_')}`;
  const enLabel = _.get(json, 'nameEn');
  const zhLabel = _.get(json, 'nameZh');
  const jaLabel = _.get(json, 'nameJp');
  i18n.addResource('en', 'translation', key, enLabel);
  i18n.addResource('zh-TW', 'translation', key, zhLabel);
  i18n.addResource('ja', 'translation', key, jaLabel);
  const value = _.get(json, 'layoutId');
  return {
    label: value,
    value: value,
    extra: _.get(json, 'subLayout')
  };
}

export type CreativesWithPagination = {
  pagination: Pagination;
  creatives: CreativeListRecord[];
};

export class RestfulCreativeWebService implements CreativeWebService {
  restClient: AxiosInstance;

  constructor(restClient: AxiosInstance = client) {
    this.restClient = restClient;
  }

  async getCreativesByCampaignId(
    campaignId: number,
    isDraft: boolean
  ): Promise<CreativeOfCampaign[]> {
    const response = await this.restClient.get(
      `/v2/rtb-campaigns/${campaignId}/creatives?draft=${isDraft}`
    );
    return wrapCreativeWatingBindList(response.data.records);
  }

  async getWatingBindCreatives(
    payload: WaitingBindGetParams
  ): Promise<CreativeOfCampaign[]> {
    const {
      channel,
      channelGroupId,
      advertiserId,
      adType,
      adTypeId,
      supportSchedule
    } = payload;
    let apiPath: string;
    if (!L1ObjectChannel[channel]) {
      apiPath = `/v2/l3-object/waiting-bind?advertiser_id=${advertiserId}&channel_id=${channelGroupId}`;
    } else {
      apiPath = `/v2/l3-object/waiting-bind?advertiser_id=${advertiserId}&channel=${channel}`;
    }

    if (adTypeId) {
      apiPath = `${apiPath}&ad_type_id=${adTypeId}`;
    } else if (adType) {
      apiPath = `${apiPath}&ad_type=${adType}`;
    }

    if (supportSchedule !== undefined) {
      apiPath = `${apiPath}&support-schedule=${supportSchedule}`;
    }
    const response = await this.restClient.get(apiPath);
    return wrapCreativeWatingBindList(response.data.records);
  }

  async reviewCreatives(creativeIds: number[], adxs: string[]): Promise<void> {
    return this.restClient.post(`/v2/creatives/review`, {
      creativeIds: creativeIds,
      adxs: adxs
    });
  }

  async getCreative(creativeId: number): Promise<Creative> {
    const response = await this.restClient.get(`v2/creatives/${creativeId}`);
    return wrapCreative(response.data);
  }

  async createCreative(creativeformData: FormData): Promise<{
    creativeId: number;
  }> {
    const response = await this.restClient.post(
      'v2/creatives/',
      creativeformData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return {
      creativeId: _.get(response.data, 'creative.creativeId', 0)
    };
  }

  async createCreativesByJson(creatives: any): Promise<
    | {
        data: any[];
        error: {
          error_message: string | null;
          name: string;
        }[];
      }
    | undefined
  > {
    const response = await this.restClient.post(
      'v2/creatives/batch-create',
      creatives
    );
    return response.data;
  }

  async updateCreative(creativeformData: FormData): Promise<number> {
    const response = await this.restClient.post(
      `v2/creatives/${creativeformData.get('creative.id')}`,
      creativeformData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return _.get(response.data, 'creative.creativeId', 0);
  }

  async getCreatives(
    creativeType: CreativeType,
    advertiserId: number | undefined,
    pageable: Pageable,
    search?: string,
    status?: CreativeStatusFilter
  ): Promise<CreativesWithPagination> {
    let { page, sizePerPage, sort, direction } = pageable;
    direction = direction ? direction : 'desc';
    let apiPath = `/v2/creatives?banner_type=${creativeType}&page=${page}&size=${sizePerPage}&sort=${sort},${direction}`;
    if (advertiserId) {
      apiPath = `/v2/advertisers/${advertiserId}/creatives?banner_type=${creativeType}&page=${page}&size=${sizePerPage}&sort=${sort},${direction}`;
    }
    if (search) {
      apiPath = `${apiPath}&search=${encodeURIComponent(search)}`;
    }
    if (status) {
      apiPath = `${apiPath}&status=${status}`;
    }
    const response = await this.restClient.get(apiPath);
    return {
      creatives: wrapCreativList(response.data.records),
      pagination: wrapPagination(response.data.pagination)
    };
  }

  async updateCreativeBindingStatus(creatives: number[], isActive: boolean) {
    if (isActive) {
      return this.restClient.post(
        `/v2/creatives/${creatives.join(',')}/enable-binding-campaigns`
      );
    }
    return this.restClient.post(
      `/v2/creatives/${creatives.join(',')}/disable-binding-campaigns`
    );
  }

  async updateCreativeBindingCampaigns(
    creativeId: number,
    campaigns: number[],
    isActive: boolean
  ) {
    return this.restClient.post(
      `/v2/creatives/${creativeId}/binding-campaigns`,
      {
        campaignIds: campaigns,
        active: isActive
      }
    );
  }

  async cloneCreatives(creatives: number[]): Promise<void> {
    return this.restClient.post(`/v2/creatives/${creatives.join(',')}/copy`);
  }

  async deleteCreative(creativeIds: number[]): Promise<void> {
    return this.restClient.delete(
      `/v2/creatives?creativeIds=${creativeIds.join(',')}`
    );
  }

  async getCreativeOptions(
    from?: string,
    to?: string
  ): Promise<SelectOptions[]> {
    let path = '/v2/creatives/options';
    const params = { from, to };
    const paramsString = Object.keys(params)
      .filter((key) => params[key] !== undefined)
      .map((key) => `${key}=${params[key]}`)
      .join('&');
    const response = await this.restClient.get(`${path}?${paramsString}`);
    return _.get(response, 'data.records', []);
  }

  async getCreativeSizeOptions(): Promise<SelectOptions[]> {
    const response = await this.restClient.get(
      '/v2/creatives/dimensions/options'
    );
    return _.get(response, 'data.records', []);
  }

  async getPpsLayoutId() {
    const response = await this.restClient.get('/v2/creatives/pps-layoutIds');
    return _.get(response, 'data.records', []).map((json) =>
      wrapPpsLayoutId(json)
    );
  }

  async getPpsThumbnail(creativeId) {
    const response = await this.restClient.get(
      `/v2/creatives/${creativeId}/pps-thumb`
    );
    return _.get(response, 'data.records', []);
  }

  async getCreativeNativeToBannerTemplate() {
    const response = await this.restClient.get(
      '/v2/creatives/native-to-banner/templates'
    );
    return _.get(response, 'data.records', []).filter(
      (url) =>
        url.includes('300-250') ||
        url.includes('320-100') ||
        url.includes('300-600')
    );
  }

  async getBindingOptions(): Promise<SelectOptions[]> {
    const response = await this.restClient.get('/v2/creatives/binding/options');
    return response.data.records;
  }

  async generateRectImgBySquareImg(imageUrl: string): Promise<string> {
    const response = await this.restClient.post(
      `/v2/images/square-to-rectangle?url=${imageUrl}`
    );
    return _.get(response.data, 'imageUrl');
  }

  async getAdneonCreativePreview(data: any): Promise<any> {
    const response = await this.restClient.post(
      '/v2/creatives/adNeon/preview',
      data
    );
    return response.data;
  }

  async publicVideoAssetId(
    videoAssetId: string,
    creativeType: CreativeType
  ): Promise<string> {
    const response = await this.restClient.get(
      `/v2/creatives/publishVideo?assetId=${videoAssetId}&bannerType=${creativeType}`
    );
    return response.data.videoUrl;
  }

  async getCreativeSizesByAdFormat(adFormat: string): Promise<CreativeSize[]> {
    const response = await this.restClient.get(
      `/v2/creatives/creative-size?adFormat=${adFormat}`
    );
    return response.data.records;
  }

  async checkCreativeSupportSchedule(creativeId: number): Promise<boolean> {
    const response = await this.restClient.get(
      `/v2/creatives/${creativeId}/enable-time-setting`
    );
    return response.data.isEnable;
  }

  async getCreativeApprovalConfigs(): Promise<CreativeApprovalConfig[]> {
    const response = await this.restClient.get(
      '/v2/banner-types/approval-configs'
    );
    return response.data.records;
  }

  async getCreativeBindingStatus(
    creativeId: number
  ): Promise<CreativeBindingCampaign[]> {
    const response = await this.restClient.get(
      `v2/creatives/${creativeId}/binding-campaigns-detail`
    );
    return response.data.records;
  }
}
